import React, { useEffect, useState } from "react";
import { Button, styled, Box, Typography } from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import { GET_GSC_SITES } from "../../gql/queries/gsc";

const GCSSitesWrapper = styled("section")(({ theme }) => ({
  marginBottom: "0px",

  "& .sitesCard": {
    marginTop: "20px",
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#ffffff",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "6px",
    padding: "24px",
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    boxShadow: "none",
  },

  "& h2": {
    fontSize: "20px",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: "10px",
  },

  "& .sitesCount": {
    fontSize: "13px",
    color: theme.palette.text.secondary,
    marginBottom: "18px",
  },

  "& .sitesList": {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  "& .siteItem": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "14px",
    padding: "12px 14px",
    borderRadius: "6px",
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#f8f9fa",
    // borderLeft: `4px solid ${theme.palette.primary.main}`,
  },

  "& .siteItem.limited": {
    borderLeftColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light,
  },

  "& .siteUrl": {
    fontSize: "13px",
    fontWeight: 600,
    color: theme.palette.text.primary,
    wordBreak: "break-all",
    flex: 1,
  },

  "& .permission": {
    fontSize: "12px",
    padding: "4px 8px",
    borderRadius: "4px",
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    textTransform: "capitalize",
    whiteSpace: "nowrap",
  },

  "& .permission.warning": {
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },

  "& .permissionNote": {
    fontSize: "11px",
    fontStyle: "italic",
    marginLeft: "6px",
  },

  "& .loading": {
    textAlign: "center",
    padding: "30px",
    color: theme.palette.text.secondary,
    fontSize: "14px",
  },

  "& .errorBox": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    borderRadius: "6px",
    backgroundColor: theme.palette.mode === "dark" ? "rgba(244,67,54,0.2)" : "#f8d7da",
    color: theme.palette.error.dark,
    fontSize: "13px",
  },

  "& .retryBtn": {
    fontSize: "13px",
    fontWeight: 600,
    padding: "6px 14px",
  },

  "& .emptyState": {
    textAlign: "center",
    padding: "30px",
    color: theme.palette.text.secondary,
  },

  "& .helpText": {
    fontSize: "12px",
    marginTop: "6px",
    color: theme.palette.text.disabled,
  },

  "@media (max-width:600px)": {
    "& .siteItem": {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
}));

function GCSSites({ email }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [loadSites] = useLazyQuery(GET_GSC_SITES, {
    fetchPolicy: "network-only",
    onCompleted: data => {
      if (data?.gscSites?.success) {
        setSites(data.gscSites.sites || []);
        setError(null);
      } else {
        setError("Failed to fetch sites");
      }
      setLoading(false);
    },
    onError: err => {
      setError(err.message || "Failed to fetch Google Search Console sites");
      setLoading(false);
    },
  });

  useEffect(() => {
    if (!email) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    loadSites({ variables: { email } });
  }, [email, loadSites]);

  const retryFetch = () => {
    setLoading(true);
    setError(null);
    loadSites({ variables: { email } });
  };

  if (loading) {
    return (
      <GCSSitesWrapper>
        <Box className="sitesCard">
          <Typography variant="h2">Google Search Console Sites</Typography>
          <Box className="loading">Loading sites…</Box>
        </Box>
      </GCSSitesWrapper>
    );
  }

  if (error) {
    return (
      <GCSSitesWrapper>
        <Box className="sitesCard">
          <Typography variant="h2">Google Search Console Sites</Typography>
          <Box className="errorBox">
            <Typography component="span">{error}</Typography>
            <Button className="retryBtn" onClick={retryFetch}>
              Retry
            </Button>
          </Box>
        </Box>
      </GCSSitesWrapper>
    );
  }

  return (
    <GCSSitesWrapper>
      <Box className="sitesCard">
        <Typography variant="h2">Google Search Console Sites</Typography>
        <Box className="sitesCount">You have access to {sites.length} site(s)</Box>

        {sites.length > 0 ? (
          <Box className="sitesList">
            {sites.map(site => {
              const permission = site.permissionLevel?.toLowerCase() || "unknown";
              const canQuery = ["restricted", "full", "owner"].includes(permission);

              return (
                <Box key={site.siteUrl} className={`siteItem ${!canQuery ? "limited" : ""}`}>
                  <Box className="siteUrl">{site.displayUrl || site.siteUrl}</Box>

                  <Box className={`permission ${!canQuery ? "warning" : ""}`}>
                    {site.permissionLevel}
                    {!canQuery && <span className="permissionNote">(Limited access)</span>}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box className="emptyState">
            <Typography variant="body1">No sites found in your Google Search Console account.</Typography>

            <Typography variant="body2" className="helpText">
              Verify at least one property in Google Search Console.
            </Typography>
          </Box>
        )}
      </Box>
    </GCSSitesWrapper>
  );
}

export default GCSSites;
